import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PaymentPage from './pages/PaymentPage';

import ChatApp from './ChatApp';
import BackofficeApp from './backoffice/BackofficeApp';
import Login from './backoffice/components/Login';
import ProtectedAdminRoute from './backoffice/components/ProtectedAdminRoute';
import './App.css';
import UserDashboardPage from './pages/UserDashboardPage';
import ThankYouPage from './pages/ThankYouPage';
import CookieConsentBanner from './components/landing/CookieConsentBanner';
import { applySeo, clearJsonLd, setJsonLd, type SeoMetadata } from './utils/seo.ts';

const BASE_URL = 'https://virtual.tarot-online.com.pt';

const HOME_SEO: SeoMetadata = {
  title: 'Tarot Virtual Grátis Online — Tira as Cartas Agora | Tarot Online',
  description:
    'Tarot virtual grátis. Tira as cartas online sem registo e descobre o que o destino reserva. Para consulta com tarólogo real, visita app.tarot-online.com.pt.',
  canonical: `${BASE_URL}/`,
  robots: 'index,follow',
  ogType: 'website',
  ogTitle: 'Tarot Virtual Grátis — Tira as Cartas Online',
  ogDescription:
    'Tira as cartas grátis e sem registo. Tarot online imediato em português.',
  ogImage: `${BASE_URL}/og-image.jpg`,
  twitterCard: 'summary_large_image',
};

const PRIVATE_SEO: SeoMetadata = {
  title: 'Tarot Virtual',
  description: 'Área reservada da plataforma Tarot Virtual.',
  canonical: `${BASE_URL}/`,
  robots: 'noindex,nofollow',
  ogType: 'website',
  ogTitle: 'Tarot Virtual',
  ogDescription: 'Área reservada da plataforma Tarot Virtual.',
  ogImage: `${BASE_URL}/og-image.jpg`,
  twitterCard: 'summary',
};

function RouteSeoManager() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const isHome = path === '/';
    const isPrivateRoute =
      path === '/chat' ||
      path === '/user-dashboard' ||
      path === '/reset-password' ||
      path === '/admin/login' ||
      path.startsWith('/backoffice/') ||
      path.startsWith('/payment/');

    if (isHome) {
      applySeo(HOME_SEO);

      const websiteSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Tarot Virtual',
        url: `${BASE_URL}/`,
        inLanguage: 'pt-PT',
      };

      const organizationSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Tarot Virtual',
        url: `${BASE_URL}/`,
        email: 'geral@tarot-online.com.pt',
      };

      setJsonLd('seo-website-schema', websiteSchema);
      setJsonLd('seo-organization-schema', organizationSchema);
      return;
    }

    clearJsonLd('seo-website-schema');
    clearJsonLd('seo-organization-schema');

    if (isPrivateRoute) {
      applySeo({
        ...PRIVATE_SEO,
        canonical: `${BASE_URL}${path}`,
      });
      return;
    }

    applySeo({
      ...PRIVATE_SEO,
      canonical: `${BASE_URL}${path}`,
      title: 'Tarot Virtual | Página',
      description: 'Plataforma Tarot Virtual.',
    });
  }, [location.pathname]);

  return null;
}

function AppRoutes() {
  return (
    <>
      <RouteSeoManager />
      <Routes>
        {/* Landing page as default */}
        <Route path="/" element={<LandingPage />} />

        {/* Password reset page */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Payment page - requires authentication */}
        <Route path="/payment/:packageId" element={<PaymentPage />} />

        {/* Thank you page */}
        <Route path="/payment/checkout" element={<ThankYouPage />} />
        {/* <Route path="/payment/checkout-refmb" element={<ThankYouPageRefMB />} /> */}

        {/* Chat application */}
        <Route path="/chat" element={<ChatApp />} />

        {/* User dashboard */}
        <Route path="/user-dashboard" element={<UserDashboardPage />} />

        {/* Backoffice login */}
        <Route path="/admin/login" element={<Login />} />

        {/* Protected backoffice routes */}
        <Route
          path="/backoffice/*"
          element={
            <ProtectedAdminRoute>
              <BackofficeApp />
            </ProtectedAdminRoute>
          }
        />

        {/* Fallback redirect to landing */}
        {/* <Route path="*" element={<Navigate to="/" replace />} /> */}
      </Routes>
    </>
  );
}

function ConditionalCookieBanner() {
  const location = useLocation();
  if (location.pathname === '/chat') return null;
  return <CookieConsentBanner />;
}

function App() {

  return (
    <BrowserRouter>
      <AppRoutes />
      <ConditionalCookieBanner />
    </BrowserRouter>
  );
}

export default App;
